import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedEntries } from '../lib/featured.js';

function getMediaKind(item) {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(item.ext)) return 'image';
  if (['mp4', 'webm'].includes(item.ext)) return 'video';
  if (['ogg', 'mp3'].includes(item.ext)) return 'audio';
  return '';
}

function MetaTags({ item }) {
  if (!item.game && !item.date) return null;
  return (
    <div className="asset-meta-tags">
      {item.game && <span className="asset-meta-tag">{item.game}</span>}
      {item.date && <span className="asset-meta-tag">{item.date}</span>}
    </div>
  );
}

function CaptionHeader({ item, category, titleTag: TitleTag = 'div' }) {
  const hasLink = Boolean(item.primaryUrl);
  const hasBlog = Boolean(item.blogRaw);
  const blogTo = `/dev-blog/${category}/${encodeURIComponent(item.name)}`;

  if (!hasLink && !hasBlog) {
    return <TitleTag className="asset-title">{item.name}</TitleTag>;
  }

  return (
    <div className="asset-caption-header">
      <TitleTag className="asset-title">{item.name}</TitleTag>
      <div className="asset-caption-actions">
        {hasBlog && (
          <Link to={blogTo} className="asset-links-btn">
            Dev Blog
          </Link>
        )}
        {hasLink && (
          <a
            href={item.primaryUrl}
            className="asset-links-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Link
          </a>
        )}
      </div>
    </div>
  );
}

export default function FeaturedWorks({ category, title = 'Featured Works', layout = 'grid' }) {
  const entries = getFeaturedEntries(category);

  if (!entries.length) {
    return null;
  }

  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const isLightboxOpen = lightboxIndex >= 0 && lightboxIndex < entries.length;
  const [isClosing, setIsClosing] = useState(false);
  const [navDirection, setNavDirection] = useState('next');
  const closeTimerRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchActiveRef = useRef(false);
  const scrollYLockRef = useRef(0);

  function openAt(index) {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsClosing(false);
    setLightboxIndex(index);
  }

  function closeLightbox() {
    if (!isLightboxOpen) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setLightboxIndex(-1);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, 220);
  }

  function goPrev() {
    if (!entries.length) return;
    setNavDirection('prev');
    setLightboxIndex((prev) => (prev + entries.length - 1) % entries.length);
  }

  function goNext() {
    if (!entries.length) return;
    setNavDirection('next');
    setLightboxIndex((prev) => (prev + 1) % entries.length);
  }

  function onTouchStart(e) {
    if (!isLightboxOpen) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchActiveRef.current = true;
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
  }

  function onTouchMove() {
    // no-op: we only need end, but prevent passive scroll if horizontal intent is strong
  }

  function onTouchEnd(e) {
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) return;
    const dx = t.clientX - touchStartXRef.current;
    const dy = t.clientY - touchStartYRef.current;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const minSwipe = 40; // px
    if (absDx > absDy && absDx > minSwipe) {
      if (dx < 0) {
        goNext();
      } else {
        goPrev();
      }
    }
  }

  useEffect(() => {
    if (!isLightboxOpen) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLightboxOpen, entries.length]);

  useEffect(() => {
    const body = document.body;
    if (isLightboxOpen) {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      scrollYLockRef.current = scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overscrollBehavior = 'none';
      body.style.touchAction = 'none';
      body.classList.add('no-scroll');
    } else {
      if (body.style.position === 'fixed') {
        const y = scrollYLockRef.current || 0;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.overscrollBehavior = '';
        body.style.touchAction = '';
        body.classList.remove('no-scroll');
        window.scrollTo(0, y);
      } else {
        body.classList.remove('no-scroll');
      }
    }
    return () => {
      const y = scrollYLockRef.current || 0;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overscrollBehavior = '';
      body.style.touchAction = '';
      body.classList.remove('no-scroll');
      if (isLightboxOpen) {
        window.scrollTo(0, y);
      }
    };
  }, [isLightboxOpen]);

  const isRows = layout === 'rows';

  return (
    <section className={`featured-works${isRows ? ' featured-works-rows' : ''}`}>
      <h2>{title}</h2>
      {!isRows && (
        <p className="gallery-hint">
          Click on any image to view more information.
        </p>
      )}
      <div className={isRows ? 'featured-rows' : 'gallery-grid'}>
        {entries.map((item, index) => {
          const key = item.path;
          const kind = getMediaKind(item);
          const isImage = kind === 'image';
          const isVideo = kind === 'video';
          const isAudio = kind === 'audio';

          if (isRows) {
            return (
              <article className="featured-row" key={key}>
                {(isImage || isVideo) && (
                  <div className="featured-row-media">
                    {isImage && (
                      <img className="pixelated" src={item.url} alt={item.name} loading="lazy" />
                    )}
                    {isVideo && (
                      <video className="pixelated" src={item.url} loop muted playsInline />
                    )}
                  </div>
                )}
                {isAudio && (
                  <div className="featured-row-media featured-row-audio">
                    <audio src={item.url} controls />
                  </div>
                )}
                <div className="featured-row-body">
                  <MetaTags item={item} />
                  <CaptionHeader item={item} category={category} titleTag="h3" />
                  {item.descHtml && (
                    <div className="asset-description" dangerouslySetInnerHTML={{ __html: item.descHtml }} />
                  )}
                </div>
              </article>
            );
          }

          return (
            <figure className="gallery-item" key={key}>
              {(isImage || isVideo) && (
                <button
                  type="button"
                  className="gallery-thumb"
                  onClick={() => openAt(index)}
                  aria-label={`Open ${item.name}`}
                >
                  {isImage && (
                    <img className="pixelated" src={item.url} alt={item.name} loading="lazy" />
                  )}
                  {isVideo && (
                    <video className="pixelated" src={item.url} loop muted playsInline />
                  )}
                </button>
              )}
              {isAudio && (
                <audio src={item.url} controls />
              )}
              <figcaption>
                <CaptionHeader item={item} category={category} />
              </figcaption>
            </figure>
          );
        })}
      </div>

      {!isRows && isLightboxOpen && (
        <div className={`lightbox-overlay${isClosing ? ' closing' : ''}`} role="dialog" aria-modal="true" aria-label="Featured work viewer">
          <button className="lightbox-backdrop" aria-label="Close" onClick={closeLightbox} />
          <div className="lightbox-content" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
            <button className="lightbox-nav prev" onClick={goPrev} aria-label="Previous">‹</button>
            <button className="lightbox-nav next" onClick={goNext} aria-label="Next">›</button>
            {(() => {
              const current = entries[lightboxIndex];
              const kind = getMediaKind(current);
              const isImage = kind === 'image';
              const isVideo = kind === 'video';
              return (
                <figure className="lightbox-figure">
                  <div
                    key={current.path}
                    className={`media-wrapper ${navDirection === 'prev' ? 'enter-from-left' : 'enter-from-right'}`}
                  >
                    {isImage && (
                      <img className="pixelated" src={current.url} alt={current.name} />
                    )}
                    {isVideo && (
                      <video className="pixelated" src={current.url} controls autoPlay playsInline />
                    )}
                  </div>
                  <figcaption>
                    <MetaTags item={current} />
                    <CaptionHeader item={current} category={category} />
                    {current.descHtml && (
                      <div className="asset-description" dangerouslySetInnerHTML={{ __html: current.descHtml }} />
                    )}
                  </figcaption>
                </figure>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
}
